import React, { useState, useCallback } from "react";
import { Search, Message, Modal, Button, Table } from "semantic-ui-react";
import { debounce } from "lodash";
import axios from "../../apis/dvdlocator"; // Adjust import path as needed

/**
 * SupplyUnitSearch component - Implementation-specific search for supply units
 * Handles search, selection, and equivalent confirmation internally
 * @param {Object} props
 * @param {Array} props.existingSupplyUnits - Array of currently selected supply units
 * @param {Function} props.onSupplyUnitsAdd - Callback when supply units are added
 * @param {string} props.placeholder - Placeholder text for search input
 * @param {number} props.minCharacters - Minimum characters before search triggers
 * @param {number} props.debounceMs - Debounce delay in milliseconds
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.fluid - Whether search should be fluid width
 */
export const SupplyUnitSearch = ({
  existingSupplyUnits = [],
  onSupplyUnitsAdd,
  placeholder = "Search by UPC or Title",
  minCharacters = 2,
  debounceMs = 500,
  className = "",
  fluid = true,
  ...searchProps
}) => {
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchError, setSearchError] = useState("");
  const [confirmationModal, setConfirmationModal] = useState({
    open: false,
    unit: null,
    equivalents: [],
  });

  // Search for supply units
  const searchSupplyUnits = async (query) => {
    try {
      const results = await axios.get("/supply", { params: { q: query } });
      return results.data;
    } catch (error) {
      throw error;
    }
  };

  // Get equivalent supply units for a given inventory ID
  const getEquivalentSupplyUnits = async (inventoryId, selectedUnitUpc) => {
    try {
      const res = await axios.get(`/supply?inventoryId=${inventoryId}`);
      return res.data.filter((unit) => unit.upc !== selectedUnitUpc);
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  // Check if a supply unit is already selected
  const isSupplyUnitAlreadySelected = (result) => {
    const upc = result.description || result.id;
    return existingSupplyUnits.some((unit) => unit.upc === upc);
  };

  // Format search results for Semantic UI
  const formatSearchResults = (results) => {
    return results.map((item) => ({
      title: item.supply_title || item.name || item.title,
      description: item.upc,
      id: item.upc,
      // Shown up front so users pick a unit whose part number fits. Lowercase
      // because Semantic passes unknown result keys through to the DOM.
      partnumber: item.part_number || "",
    }));
  };

  // Create a debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!query || query.length < minCharacters) {
        setSearchResults([]);
        setSearchError("");
        return;
      }

      setLoading(true);
      setSearchError("");

      try {
        const results = await searchSupplyUnits(query);
        const formattedResults = formatSearchResults(results || []);
        setSearchResults(formattedResults);
      } catch (err) {
        console.error(err);
        setSearchError(
          err.response?.data?.message ||
            err.message ||
            "An error occurred while searching. Please try again."
        );
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs),
    [minCharacters, debounceMs]
  );

  // Handle supply unit selection
  const handleResultSelect = async (e, { result }) => {
    // Find the original item from search results
    const originalItem = searchResults.find((item) => result.id === item.id);

    if (!originalItem) return;

    const upc = result.id;

    // Check if already selected
    if (isSupplyUnitAlreadySelected(result)) {
      return;
    }

    // Clear search immediately
    setSearchValue("");
    setSearchResults([]);

    // Find the original supply unit data for this UPC
    try {
      const supplyUnitData = await axios.get(`/supply?upc=${upc}`);
      const selectedUnit = supplyUnitData.data[0];

      if (!selectedUnit) {
        console.error("Could not find supply unit data");
        return;
      }

      // Getting all supply units with this inventory ID if they have one
      if (selectedUnit.inventory_id) {
        const equivalents = await getEquivalentSupplyUnits(
          selectedUnit.inventory_id,
          upc
        );

        if (equivalents.length > 0) {
          setConfirmationModal({
            open: true,
            unit: selectedUnit,
            equivalents,
          });
          return;
        }
      }

      // If no equivalents, add the unit directly
      addSupplyUnitsToInventory([selectedUnit]);
    } catch (error) {
      console.error("Error fetching supply unit data:", error);
      setSearchError("Error adding supply unit. Please try again.");
    }
  };

  // Add supply units to inventory
  const addSupplyUnitsToInventory = (units) => {
    if (onSupplyUnitsAdd) {
      onSupplyUnitsAdd(units);
    }

    setConfirmationModal({ open: false, unit: null, equivalents: [] });
  };

  const handleSearchChange = (e, { value }) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleConfirmationAccept = () => {
    const { unit, equivalents } = confirmationModal;
    addSupplyUnitsToInventory([...equivalents, unit]);
  };

  const handleConfirmationCancel = () => {
    setConfirmationModal({ open: false, unit: null, equivalents: [] });
  };

  // Custom result renderer for supply units
  const customResultRenderer = (result) => {
    const isSelected = isSupplyUnitAlreadySelected(result);
    return (
      <div
        style={{
          opacity: isSelected ? 0.5 : 1,
          cursor: isSelected ? "not-allowed" : "pointer",
          padding: "8px",
          color: isSelected ? "#666" : "inherit",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div>{result.title}</div>
            <div style={{ fontSize: "0.8em", color: "#666" }}>
              UPC: {result.description} · Part #: {result.partnumber || "none"}
            </div>
          </div>
          {isSelected && (
            <span style={{ fontSize: "0.8em", color: "#666" }}>
              (Already in Inventory)
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Search
        fluid={fluid}
        input={{ fluid: fluid }}
        loading={loading}
        onResultSelect={handleResultSelect}
        onSearchChange={handleSearchChange}
        results={searchResults}
        resultRenderer={customResultRenderer}
        value={searchValue}
        minCharacters={minCharacters}
        placeholder={placeholder}
        className={className}
        {...searchProps}
      />
      {searchError && (
        <Message error content={searchError} style={{ marginTop: "5px" }} />
      )}

      {/* Confirmation Modal for Equivalent Units */}
      <Modal open={confirmationModal.open} size="small">
        <Modal.Header>Add Equivalent Supply Units</Modal.Header>
        <Modal.Content>
          <p>
            This supply unit has {confirmationModal.equivalents.length}{" "}
            equivalent items. Adding it will add all the following supply units
            to this inventory:
          </p>
          <Table basic="very">
            <Table.Body>
              {confirmationModal.unit && (
                <Table.Row positive key={confirmationModal.unit.upc}>
                  <Table.Cell>{confirmationModal.unit.upc}</Table.Cell>
                  <Table.Cell>{confirmationModal.unit.supply_title}</Table.Cell>
                  <Table.Cell>{`(selected)`}</Table.Cell>
                </Table.Row>
              )}
              {confirmationModal.equivalents.map((unit) => (
                <Table.Row warning key={unit.upc}>
                  <Table.Cell>{unit.upc}</Table.Cell>
                  <Table.Cell>{unit.supply_title}</Table.Cell>
                  <Table.Cell>{`(equivalent)`}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleConfirmationCancel}>Cancel</Button>
          <Button primary onClick={handleConfirmationAccept}>
            Accept
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};
